import type { TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { useMemo } from 'react';
import type {
  IVisualizationHeader,
  IVisualizationHeaderProps,
} from '@/common/api/types/loopVisualization';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import {
  DEFAULT_BASE_COLOR,
  DEFAULT_FALLBACK_COLOR,
  getPaletteSwatches,
} from '@/features/visualizations/utils/colors';
import { generateHeaders } from '../utils/generateHeaders';

type ColoredHeader = IVisualizationHeaderProps & { headerColor: string };

function applyHeaderColors({
  headers,
  paletteColors,
  orderedHeaderValues,
  fallbackColor = DEFAULT_BASE_COLOR,
}: {
  headers: IVisualizationHeaderProps[];
  paletteColors: string[];
  orderedHeaderValues: string[];
  fallbackColor?: string;
}): ColoredHeader[] {
  if (orderedHeaderValues.length > 0) {
    const orderedIndexByHeaderId: Record<string, number> = {};
    orderedHeaderValues.forEach((id, index) => {
      orderedIndexByHeaderId[id] = index;
    });
    return headers.map((header, headerIndex) => {
      const headerId = header.id?.toString() ?? '';
      const colorIndex = orderedIndexByHeaderId[headerId] ?? headerIndex;
      return {
        ...header,
        headerColor: paletteColors[colorIndex % paletteColors.length] || fallbackColor,
      };
    });
  }

  return headers.map((header, index) => ({
    ...header,
    headerColor: paletteColors[index % paletteColors.length] || fallbackColor,
  }));
}

export const useTimelineMainHeaders = ({
  timelineHeaderOptions,
  colorConfig,
  header1FieldId,
  effectiveColorByField,
  orderedHeaderValues,
  includeNullHeader,
}: {
  timelineHeaderOptions: IVisualizationHeader;
  colorConfig: TimelineLayoutDTO['colorConfig'];
  header1FieldId: string;
  effectiveColorByField: string;
  orderedHeaderValues: string[];
  includeNullHeader: boolean;
}): ColoredHeader[] => {
  const ideaManagerEnabled = useFlag('ideaManagerEnabled');

  return useMemo(() => {
    const headers = generateHeaders({
      header: timelineHeaderOptions,
      items: [],
      includeNullHeader,
      includeDateHeader: false,
      includeCustomerDisplayValues: ideaManagerEnabled,
    });

    const paletteSwatches = getPaletteSwatches(colorConfig);
    const matchHeaderColors = colorConfig?.matchHeaderColors ?? true;
    const header1MatchesColorField = effectiveColorByField === header1FieldId;

    let paletteColors: string[];
    if (header1MatchesColorField && matchHeaderColors) {
      paletteColors = paletteSwatches.itemColors;
    } else {
      const desaturatedIndex = colorConfig?.desaturatedHeaderColorIndex ?? 0;
      paletteColors = [paletteSwatches.headerColors[desaturatedIndex] || DEFAULT_FALLBACK_COLOR];
    }

    if (!header1MatchesColorField || !matchHeaderColors) {
      return applyHeaderColors({
        headers,
        paletteColors,
        orderedHeaderValues: [],
      });
    }
    return applyHeaderColors({
      headers,
      paletteColors,
      orderedHeaderValues,
    });
  }, [
    timelineHeaderOptions,
    colorConfig,
    header1FieldId,
    effectiveColorByField,
    orderedHeaderValues,
    includeNullHeader,
    ideaManagerEnabled,
  ]);
};

export const useTimelineSubHeaders = ({
  timelineSubHeaderOptions,
  colorConfig,
  header2FieldId,
  effectiveColorByField,
  orderedHeaderValues,
  includeNullHeader,
}: {
  timelineSubHeaderOptions: IVisualizationHeader;
  colorConfig: TimelineLayoutDTO['colorConfig'];
  header2FieldId: string;
  effectiveColorByField: string;
  orderedHeaderValues: string[];
  includeNullHeader: boolean;
}): ColoredHeader[] => {
  const ideaManagerEnabled = useFlag('ideaManagerEnabled');

  return useMemo(() => {
    const headers = generateHeaders({
      header: timelineSubHeaderOptions,
      items: [],
      includeNullHeader,
      includeDateHeader: false,
      includeCustomerDisplayValues: ideaManagerEnabled,
    });

    if (headers.length === 0) return headers as ColoredHeader[];

    const matchHeaderColors = colorConfig?.matchHeaderColors ?? true;
    const header2MatchesColorField = effectiveColorByField === header2FieldId;

    if (!header2MatchesColorField || !matchHeaderColors) {
      return headers.map((header) => ({ ...header, headerColor: '' }));
    }

    const paletteSwatches = getPaletteSwatches(colorConfig);

    return applyHeaderColors({
      headers,
      paletteColors: paletteSwatches.itemColors,
      orderedHeaderValues,
    });
  }, [
    timelineSubHeaderOptions,
    colorConfig,
    header2FieldId,
    effectiveColorByField,
    orderedHeaderValues,
    includeNullHeader,
    ideaManagerEnabled,
  ]);
};
